import clsx from 'clsx';
import { type MouseEvent as ReactMouseEvent } from 'react';
import type {
  OptionProps as ReactSelectOptionProps,
  GroupBase,
} from 'react-select';

import { CheckmarkSmallIcon } from '@dynatrace/strato-icons';

import { TextEllipsisAroundChildren } from '../../../core/components/TextEllipsisAroundChildren.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { useModality } from '../../../core/hooks/useModality.js';
import { getVirtualFocusStyles } from '../../../core/utils/getVirtualFocusStyles.js';
import { modalityStore } from '../../../core/utils/modality.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { useSelectMenuContext } from '../hooks/use-select-menu-context.js';
import { SelectActions } from '../state/select-menu-state-reducer.js';
import * as styles from '../styles/Select.sty.js';
import type { BaseOption, BaseGroup } from '../types.js';

export type OptionProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectOptionProps<OptionValue, isMulti, Group>;

export function Option<
  OptionValue,
  isMulti extends boolean,
  Group extends BaseGroup<OptionValue>,
>(props: Readonly<OptionProps<BaseOption<OptionValue>, isMulti, Group>>) {
  const {
    children,
    isSelected,
    innerProps: {
      onMouseMove: _onMouseMove,
      onMouseOver: _onMouseOver,
      ...innerProps
    },
    isFocused,
    isDisabled,
    isMulti,
    selectProps,
  } = props;
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    prefix,
    suffix,
  } = props.data;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(props.data);
  const [prefixTrackingProps] = useBehavioralTrackingProps(
    prefix.componentProps ?? {},
  );
  const [suffixTrackingProps] = useBehavioralTrackingProps(
    suffix.componentProps ?? {},
  );
  const modality = useModality();
  const { dispatch } = useSelectMenuContext();

  const isVirtuallyFocused =
    modality === 'keyboard' && selectProps.focusOnOption && isFocused;
  const { className: virtualFocusClassName } = getVirtualFocusStyles(
    isVirtuallyFocused,
    isSelected && !isMulti ? 'primary' : 'neutral',
  );

  return (
    <Flex
      data-testid={dataTestId}
      role="option"
      {...innerProps}
      onMouseMove={() => modalityStore.setState('pointer')}
      className={clsx(
        'strato-select-option',
        isMulti === true ? styles.selectOptionMulti : styles.selectOptionSingle,
        virtualFocusClassName,
        {
          'strato-select-option--selected': isSelected,
          'strato-select-option--disabled': isDisabled,
          'strato-select-option--focus': isVirtuallyFocused,
        },
        consumerClassName,
      )}
      style={consumerStyle}
      onClick={(event: ReactMouseEvent<HTMLDivElement>) => {
        if (!isDisabled) {
          // Only if the option is in single select mode, we can close the menu.
          if (isMulti === false) {
            dispatch({
              type: SelectActions.CLOSE,
            });
          }
          innerProps.onClick?.(event);
        }
      }}
      aria-selected={isSelected}
      aria-label={ariaLabel}
      {...behavioralTrackingProps}
    >
      {isMulti === true ? (
        <div
          className={clsx(
            styles.selectOptionMultiIndicator({
              selected: isSelected,
            }),
          )}
        >
          {isSelected === true ? (
            <CheckmarkSmallIcon
              className={styles.selectOptionMultiIndicatorIcon}
            />
          ) : null}
        </div>
      ) : null}
      {/* Check if any of the options have a prefix defined. If they do, all other items will need to respect the space */}
      {props.selectProps.optionsHavePrefix ? (
        <span
          className={clsx(
            styles.selectOptionPrefix,
            prefix.componentProps?.className,
          )}
          style={prefix.componentProps?.style}
          data-testid={prefix.componentProps?.['data-testid']}
          {...prefixTrackingProps}
        >
          {prefix.component}
        </span>
      ) : null}
      <TextEllipsisAroundChildren>{children}</TextEllipsisAroundChildren>
      {/* For the Suffix icon, we will not need to deal with the other options. Overlaps are fine here. */}
      {suffix.component ? (
        <span
          className={clsx(
            styles.selectOptionSuffix,
            suffix.componentProps?.className,
          )}
          style={suffix.componentProps?.style}
          data-testid={suffix.componentProps?.['data-testid']}
          {...suffixTrackingProps}
        >
          {suffix.component}
        </span>
      ) : null}
    </Flex>
  );
}
